import { ThirdPartySignup, Recognition } from './types';
import { pipe } from 'fp-ts/lib/function';
import { toEntries } from 'fp-ts/lib/Record';
import { reduce } from 'fp-ts/lib/Array';
import { match as BMatch } from 'fp-ts/lib/boolean';
import { ThirdPartyBizz } from '@oresundsbron/validators';
import { Lens } from 'monocle-ts';
import { fromNullable, map, TaskOption } from 'fp-ts/lib/TaskOption';
import { ValidationResult } from '@oresundsbron/use-form';

export const isBizz = (
  recognition: Omit<Recognition, 'invalid'>
): recognition is ThirdPartyBizz & { invalid: boolean } =>
  recognition.paymentOptions === 'bizz';

export const isBusinessSkipValidation = (
  isBusiness: boolean,
  recognition: Omit<Recognition, 'invalid'>,
  skipValidation: boolean = false
): boolean =>
  isBusiness
    ? recognition.country === '' && recognition.licensePlate !== ''
    : skipValidation;

export const isInvalid = (
  recognition: Omit<Recognition, 'invalid'>,
  isBusiness: boolean = false
) =>
  isBusinessSkipValidation(
    isBusiness,
    recognition,
    !isBizz(recognition) ||
      (!!recognition.licensePlate &&
        recognition.licensePlate !== '_' &&
        !!recognition.country)
  );

export const convertErrorKeys =
  (index: number) => (errs: Record<string, string | string[]>) =>
    pipe(
      errs,
      toEntries,
      reduce({} as Record<string, string | string[]>, (res, [key, value]) => ({
        ...res,
        [`payment.paymentEntries.${index}.${key}`]: value,
      }))
    );

export const convertAllErrorKeys = <T>(
  errs: ValidationResult<T> | undefined
): TaskOption<ValidationResult<T> | undefined> =>
  pipe(
    errs ? errs : null,
    fromNullable,
    map((e) => Object.entries(e)),
    map(
      reduce({} as ValidationResult<T>, (res, [key, value]) => {
        const [index, k] = key.split('.');
        return {
          ...res,
          [`payment.paymentEntries.${index}.${k}`]: value,
        };
      })
    )
  );

const payment = Lens.fromProp<ThirdPartySignup>()('payment');
const paymentEntries =
  Lens.fromProp<ThirdPartySignup['payment']>()('paymentEntries');

export const pickTermsOfAgreement =
  Lens.fromProp<ThirdPartySignup>()('termsOfAgreement');

export const pickRecognitions = payment.compose(paymentEntries);

export const sortRecognitions = (
  recognitionA: Recognition,
  recognitionB: Recognition
) =>
  pipe(
    !recognitionA.invalid,
    BMatch(
      () =>
        pipe(
          !recognitionB.invalid,
          BMatch(
            () => 0,
            () => 1
          )
        ),
      () => -1
    )
  );
